import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "mml_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - parseInt(v, 10) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari ne déclenche pas beforeinstallprompt → on affiche un hint manuel après 8s sur les pages publiques.
    let iosTimer;
    if (isIOS()) {
      iosTimer = window.setTimeout(() => {
        if (!isStandalone() && !recentlyDismissed()) {
          setIosHelp(true);
          setShow(true);
        }
      }, 8000);
    }

    const onInstalled = () => {
      setShow(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* noop */ }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 z-[60] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
      role="dialog"
      aria-label="Installer MML"
    >
      <div className="bg-white border border-[#1F4032]/15 rounded-2xl shadow-[0_20px_50px_-15px_rgba(31,64,50,0.35)] p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1F4032] to-[#0F2A20] flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#15302A] text-sm">Installer MML sur votre appareil</div>
          {iosHelp ? (
            <div className="text-xs text-[#15302A]/70 mt-1 leading-snug">
              Touchez <Share className="inline w-3.5 h-3.5 mx-0.5 align-text-bottom" /> puis <strong>« Sur l'écran d'accueil »</strong> pour installer l'app.
            </div>
          ) : (
            <div className="text-xs text-[#15302A]/70 mt-1 leading-snug">
              Accès rapide, notifications, expérience plein écran — comme une vraie app.
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            {!iosHelp && (
              <button
                data-testid="pwa-install-btn"
                onClick={install}
                className="h-9 px-4 rounded-full bg-gradient-to-br from-[#1F4032] to-[#0F2A20] text-white text-xs font-semibold shadow-[0_6px_14px_-6px_rgba(31,64,50,0.5)] hover:from-[#2A5443] hover:to-[#1F4032] transition"
              >
                Installer
              </button>
            )}
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismiss}
              className="h-9 px-3 rounded-full text-[#15302A]/65 text-xs font-medium hover:bg-[#1F4032]/5 transition"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-full text-[#15302A]/50 hover:bg-[#1F4032]/5 transition"
          aria-label="Fermer"
          data-testid="pwa-install-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
